import React from "react";
import { Link } from "react-router-dom";
import StaticPage from "./StaticPage";
import {
  BookOpen, Library, Clock, ShoppingBag, Eye, MessageCircle,
  ArrowRight, CheckCircle2, LifeBuoy,
} from "lucide-react";

const CATEGORIES = [
  {
    icon: BookOpen,
    title: "Academic Books",
    body: "Order your child's prescribed textbooks and workbooks in a few taps — no more standing in long distribution queues.",
  },
  {
    icon: Library,
    title: "Library Books",
    body: "Explore curated reading collections and reference books approved by your child's school.",
  },
];

const BENEFITS = [
  { icon: Clock,       title: "No long queues",
    body: "Skip crowded distribution days — order online and get books delivered at your convenience." },
  { icon: ShoppingBag, title: "Easier ordering",
    body: "One clean, mobile-first interface for placing orders for one or more children." },
  { icon: Eye,         title: "Better order visibility",
    body: "See exactly where your order is — from confirmation to dispatch to delivery — without chasing anyone." },
  { icon: MessageCircle, title: "Convenient communication",
    body: "Reach out to the SchoolLane team on WhatsApp, phone or email whenever you need help." },
  { icon: LifeBuoy,    title: "Simplified issue resolution",
    body: "Raise a request and track it through to resolution — no back-and-forth with multiple counters." },
];

export default function Parents() {
  return (
    <StaticPage
      title="A simpler way to order your child's school books"
      subtitle="SchoolLane makes school book purchases faster, clearer and more convenient — starting with academic and library books."
    >
      {/* Live categories */}
      <div className="not-prose grid md:grid-cols-2 gap-6 mt-2">
        {CATEGORIES.map((c) => (
          <div key={c.title}
               className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-blue-300 hover:shadow-md transition-all duration-300">
            <div className="w-11 h-11 rounded-lg bg-blue-50 text-blue-700 grid place-items-center">
              <c.icon size={22} />
            </div>
            <div className="mt-4 flex items-center gap-2">
              <h3 className="font-heading font-semibold text-lg text-slate-900">
                {c.title}
              </h3>
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 border border-emerald-200 rounded-full px-2 py-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Live
              </span>
            </div>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.body}</p>
          </div>
        ))}
      </div>

      {/* Benefits */}
      <div className="not-prose mt-14">
        <div className="text-xs font-semibold text-blue-700 tracking-wider uppercase">
          What you get
        </div>
        <h2 className="mt-2 text-2xl sm:text-3xl font-heading font-semibold text-slate-900">
          A smoother book-buying experience
        </h2>
        <div className="grid md:grid-cols-2 gap-4 mt-6">
          {BENEFITS.map((b) => (
            <div key={b.title}
                 className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="w-11 h-11 rounded-lg bg-blue-50 text-blue-700 grid place-items-center">
                <b.icon size={20} />
              </div>
              <h3 className="mt-4 font-heading font-semibold text-slate-900">
                {b.title}
              </h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{b.body}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Assurance */}
      <div className="not-prose mt-14 rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-7">
        <div className="text-xs uppercase tracking-widest font-semibold text-blue-700">
          Only what your child's school has approved
        </div>
        <p className="mt-2 text-sm text-slate-700 leading-relaxed">
          You'll only see the book list authorised by your child's school for
          the current academic session. No guesswork, no ordering the wrong title.
        </p>
      </div>

      {/* CTAs */}
      <div className="not-prose mt-14 rounded-2xl bg-slate-900 text-white p-8 sm:p-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-300">
            Getting started
          </div>
          <div className="mt-1 text-2xl font-heading font-semibold">
            Create a parent account or sign in to start ordering.
          </div>
          <p className="mt-2 text-sm text-slate-300 max-w-lg">
            If your child's school isn't on SchoolLane yet, please ask the school
            administrator to reach out to us and we'll take it from there.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/register" data-testid="parents-cta-register"
                className="px-5 py-3 rounded-md bg-white text-slate-900 font-semibold hover:bg-slate-100 transition-colors duration-200 inline-flex items-center gap-2">
            <ShoppingBag size={16} /> Create Parent Account
          </Link>
          <Link to="/login" data-testid="parents-cta-login"
                className="px-5 py-3 rounded-md border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors duration-200 inline-flex items-center gap-2">
            Parent Login <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </StaticPage>
  );
}
